/**
 * Query and mutation hooks of the opening-cost confirmation workbench (batch 8 T11, #3032).
 *
 * The API types mirror `routes/inventory-costing.ts`. Money from the API is text with four decimals
 * (`"100.1250"`) so nothing is rounded in transit; `null` is unknown and `"0.0000"` is a confirmed zero.
 *
 * A write is sent with the caller's `Idempotency-Key`. The hook never invents, rotates or retries a key:
 * after an uncertain result the caller sends the SAME key with the SAME payload again (see
 * `features/inventory/costing/pendingOperation.ts`), which the server answers from its durable receipt.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/hooks/queryKeys';
import {
  COSTING_CONFIRM_PERMISSION,
  OwnerChangedError,
  ownerKey,
  readCostingAccess,
  scopeEquals,
  takeFence,
  useCostingAccess,
  useCostingCacheGuard,
  type CostingAccess,
  type CostingScope,
} from '@/features/inventory/costing/costingSession';

export type CostConfirmationBasis =
  | 'invoice_evidence'
  | 'physical_valuation'
  | 'supplier_price_selected'
  | 'last_receipt_selected'
  | 'catalog_cost_selected'
  | 'other';

export const COST_CONFIRMATION_BASES: readonly CostConfirmationBasis[] = [
  'invoice_evidence',
  'physical_valuation',
  'supplier_price_selected',
  'last_receipt_selected',
  'catalog_cost_selected',
  'other',
];

export type LayerSourceType =
  | 'purchase_receipt'
  | 'opening_balance'
  | 'adjustment_in'
  | 'stocktake_in'
  | 'transfer_in'
  | 'return_in';

export interface RoundedTotal {
  /** Exact sum at four decimals (cents). */
  cents4: string;
  /** The exact sum rounded once to whole cents. */
  cents: number;
}

export interface CostingSummary {
  costBasis: 'fifo';
  readAt: string;
  scope: { includeInactive: boolean };
  valuesVisible: boolean;
  quantities: { known: number; unconfirmed: number; unbacked: number; overBacked: number; unbackedConsumed: number };
  keys: { drift: number; unbacked: number; overBacked: number; negativeOnHand: number };
  openingLayers: {
    rootCount: number;
    layerCount: number;
    remainingQuantity: number;
    consumedQuantity: number;
    exhaustedRootCount: number;
    rootsWithConsumptionCount: number;
  };
  value: RoundedTotal | null;
  valueIsComplete: boolean;
}

export interface OpeningLayerHints {
  lastReceipt: { unitCostCents4: string; receivedAt: string } | null;
  catalogCost: {
    status: 'recorded' | 'unknown';
    unitCostCents: number | null;
    source: 'parts.cost_price_cents';
    recordedAt: string | null;
  };
  preferredSupplier: { supplierId: string; supplierName: string; unitCostCents: number; priceUpdatedAt: string | null } | null;
}

export interface OpeningLayer {
  layerId: string;
  partId: string;
  sku: string;
  name: string;
  warehouseId: string;
  warehouseName: string;
  sourceType: LayerSourceType;
  layerDate: string;
  enteredAt: string;
  quantityInitial: number;
  quantityRemaining: number;
  quantityConsumed: number;
  exhausted: boolean;
  costStatus: 'unconfirmed';
  unitCostCents: null;
  lineage: {
    layerCount: number;
    warehouseCount: number;
    remainingByWarehouse: { warehouseId: string; warehouseName: string; quantityRemaining: number; layerCount: number }[];
  };
  hints: OpeningLayerHints | null;
}

export interface OpeningLayersPage {
  items: OpeningLayer[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  valuesVisible: boolean;
  readAt: string;
}

export interface OpeningLayersParams {
  warehouseId?: string | undefined;
  q?: string | undefined;
  exhausted?: 'include' | 'exclude' | 'only' | undefined;
  includeInactive?: boolean | undefined;
  limit: number;
  offset: number;
}

export interface ConfirmItem {
  layerId: string;
  /** Cents with up to four decimals, as text. Always explicit: there is no default. */
  unitCostCents: string;
  basis: CostConfirmationBasis;
  note?: string;
  evidenceRef?: string;
  /** The consumed quantity the user was shown; the server refuses when more has been consumed since. */
  expectedConsumedQuantity: number;
}

export interface ConfirmRequest {
  items: ConfirmItem[];
  acknowledgeConsumedQuantity: boolean;
}

export interface ConfirmedLayer {
  layerId: string;
  confirmationId: string;
  partId: string;
  sku: string;
  name: string;
  warehouseId: string;
  basis: CostConfirmationBasis;
  unitCostCents: string;
  lineageLayerIds: string[];
  quantityRemaining: number;
  quantityConsumed: number;
  consumedAcknowledged: boolean;
  remainingValue: RoundedTotal;
  confirmedAt: string;
}

export interface ConfirmOutcome {
  replayed: boolean;
  durable: boolean;
  results: ConfirmedLayer[];
  total: {
    remainingQuantity: number;
    remainingValue: RoundedTotal;
    rowRoundedSumCents: number;
    roundingDifferenceCents: number;
  };
  consumedUnitsAffected: number;
}

export type ReconcileKind = 'unbacked' | 'over_backed' | 'negative_on_hand' | 'none';

export interface ReconcileKey {
  partId: string;
  warehouseId: string;
  sku: string;
  name: string;
  warehouseName: string;
  onHand: number;
  layerQuantity: number;
  drift: number;
  kind: ReconcileKind;
  action: 'none' | 'would_create_layer' | 'created_layer' | 'over_backed' | 'negative_on_hand' | 'replayed';
  unbackedQuantity: number;
  layerId: string | null;
}

export interface ReconcileOutcome {
  applied: boolean;
  replayed: boolean;
  durable: boolean;
  keys: ReconcileKey[];
  createdLayers: { layerId: string; partId: string; warehouseId: string; quantity: number; sourceType: string; costStatus: string }[];
  total: number | null;
  effects: {
    physicalQuantitiesChanged: false;
    costsAssigned: false;
    historicalConsumptionAltered: false;
    overBackedRepaired: false;
  };
}

/**
 * Every costing query belongs to one OWNER (`ownerKey`: session, tenant, user and the financial and confirm
 * rights), which is part of its key. A different session, tenant, user or permission set is therefore a
 * different query: it can never read, or be shown while loading, what another owner fetched.
 */
export const costingKeys = {
  all: [...queryKeys.inventory.all, 'costing'] as const,
  summary: (owner: string, includeInactive: boolean) => [...costingKeys.all, owner, 'summary', { includeInactive }] as const,
  layers: (owner: string, params: OpeningLayersParams) => [...costingKeys.all, owner, 'opening-layers', params] as const,
};

/** Position of the owner in a costing query key. */
const OWNER_INDEX = costingKeys.all.length;

const BASE = '/inventory/costing';

/** The owner of the costing queries of this render, and the clean-up of everyone else's. */
export function useCostingOwner(): { access: CostingAccess; owner: string } {
  const access = useCostingAccess();
  const owner = ownerKey(access);
  useCostingCacheGuard(owner, costingKeys.all);
  return { access, owner };
}

export function useCostingSummary(options: { includeInactive: boolean; enabled?: boolean }) {
  const { access, owner } = useCostingOwner();
  const enabled = (options.enabled ?? true) && access.canRead;
  const query = useQuery({
    queryKey: costingKeys.summary(owner, options.includeInactive),
    queryFn: () =>
      api.get<CostingSummary>(`${BASE}/summary`, { params: { includeInactive: options.includeInactive } }),
    enabled,
    // Cost figures change under other users' hands: always show the latest read.
    staleTime: 0,
  });
  // A read that this owner may not make is not shown, whatever is cached.
  return enabled ? query : { ...query, data: undefined };
}

export function useOpeningLayers(params: OpeningLayersParams, options: { enabled?: boolean } = {}) {
  const { access, owner } = useCostingOwner();
  const enabled = (options.enabled ?? true) && access.canRead;
  const query = useQuery({
    queryKey: costingKeys.layers(owner, params),
    queryFn: () =>
      api.get<OpeningLayersPage>(`${BASE}/opening-layers`, {
        params: {
          warehouseId: params.warehouseId,
          q: params.q || undefined,
          exhausted: params.exhausted,
          includeInactive: params.includeInactive,
          limit: params.limit,
          offset: params.offset,
        },
      }),
    enabled,
    // The previous page keeps the table steady while the next one loads, but only the previous page of the
    // SAME owner: another owner's rows are never a placeholder.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[OWNER_INDEX] === owner ? previous : undefined),
    staleTime: 0,
  });
  return enabled ? query : { ...query, data: undefined };
}

/**
 * Re-reads what a write may have changed, for the owner that is signed in NOW and only when that is still
 * the session that made the write. A write answered into another session refreshes nothing of it.
 */
function invalidateAfterCostWrite(queryClient: ReturnType<typeof useQueryClient>, origin: CostingScope | undefined): void {
  const access = readCostingAccess();
  if (origin !== undefined && !scopeEquals(origin, access.scope)) return;
  const current = ownerKey(access);
  void queryClient.invalidateQueries({ queryKey: costingKeys.all, predicate: (query) => query.queryKey[OWNER_INDEX] === current });
  void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stats() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.parts.lists() });
}

/** Refuses, before anything is sent, a write whose session or permission is no longer the current one. */
function assertStillCurrent(scope: CostingScope | undefined): void {
  if (scope !== undefined && takeFence(scope, COSTING_CONFIRM_PERMISSION) === null) throw new OwnerChangedError();
}

/**
 * The key and the exact payload travel together; the caller owns both across retries. `scope` is the
 * session the request was written down under: the write is refused unless it is still the current one.
 */
export interface KeyedConfirm {
  key: string;
  request: ConfirmRequest;
  scope?: CostingScope | undefined;
}

export function useConfirmOpeningCosts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, request, scope }: KeyedConfirm) => {
      assertStillCurrent(scope);
      return api.post<ConfirmOutcome>(`${BASE}/opening-layers/confirm`, request, { headers: { 'Idempotency-Key': key } });
    },
    // A refusal changes what is true (a layer was confirmed elsewhere, units were consumed): re-read.
    onSettled: (_data, _error, variables) => invalidateAfterCostWrite(queryClient, variables.scope),
  });
}

export type ReconcileRequest =
  | { apply: false; warehouseId?: string | undefined; limit?: number; offset?: number; includeInactive?: boolean }
  | { apply: true; keys: { partId: string; warehouseId: string }[] };

export function useReconcileCosting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, request, scope }: { key?: string | undefined; request: ReconcileRequest; scope?: CostingScope | undefined }) => {
      assertStillCurrent(scope);
      return api.post<ReconcileOutcome>(`${BASE}/reconcile`, request, key ? { headers: { 'Idempotency-Key': key } } : undefined);
    },
    onSettled: (_data, _error, variables) => {
      if (variables.request.apply) invalidateAfterCostWrite(queryClient, variables.scope);
    },
  });
}
